import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Accordion, Button, Icon, TextArea, Typography } from '@scale/scaleui';
import {
  ActionTakenEnum,
  ContributorFeedbackItem,
  FeedbackHelpfulness,
  truncateText,
} from '@scale/shared/types/chat/contributorFeedback';
import produce from 'immer';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { FeedbackResponseType, FeedbackType } from '@scale/shared/types/chat';
import { twMerge } from 'tailwind-merge';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ReviewLevel } from '@scale/shared-types';
import { SENIOR_LABEL_REVIEW_LEVELS } from '@scale/shared/chat/response';
import { BinaryFeedbackGroup } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/BinaryFeedbackGroup';
import { logEvent } from 'react-scale/lib/events';
import { Event } from '@scale/shared/types/events/index';
import { UnifiedFeedbackContributorActionEvent } from '@scale/shared/types/events/UnifiedFeedbackEvents/UnifiedFeedbackContributorActionEvent';
import { UnifiedFeedbackContributorRatingEvent } from '@scale/shared/types/events/UnifiedFeedbackEvents/UnifiedFeedbackContributorRatingEvent';
import _ from 'lodash';
import { JUSTIFICATION_MIN_CHARACTERS } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/constants';
import { useMeasure } from 'react-use-v13';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import FeedbackStatusDot from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/FeedbackStatusDot';
import {
  ChatTheme,
  useChatThemeStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface FeedbackActionsProps {
  className?: string;
  setIsEditing?: (isEditing: boolean) => void;
  onFeedbackAction?: (action: ActionTakenEnum) => void;
  onFeedbackRating?: (rating: FeedbackHelpfulness) => void;
}

/**
 * TODO: Need to clean up this component a bit and possibly add some props instead of relying purely on context.
 */
// eslint-disable-next-line complexity
const FeedbackActions: React.FC<FeedbackActionsProps> = ({
  className,
  setIsEditing,
  onFeedbackAction,
  onFeedbackRating,
}) => {
  const { feedbackItem } = useContext(FeedbackAccordionContext)!;
  const feedback = useContributorFeedbackStore(s => s.feedback);
  const setFeedback = useContributorFeedbackStore(s => s.setFullFeedbackState);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const deleteWorkerComment = useWorkerCommentsStore(state => state.deleteWorkerComment);
  const addFeedbackResponseToWorkerComment = useWorkerCommentsStore(
    state => state.addFeedbackResponseToWorkerComment,
  );
  const justification = feedbackItem?.action?.justification ?? '';
  const setJustification = useCallback(
    (value: string) => {
      setFeedback(
        produce(feedback, draft => {
          const targetFeedbackItem = draft.find(item => item.id === feedbackItem.id)!;
          targetFeedbackItem.action = {
            ...targetFeedbackItem.action,
            justification: value,
          };
        }),
      );
    },
    [feedback, feedbackItem, setFeedback],
  );
  const taskContext = useContext(ChatTaskContext);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const isWorkerComment = feedbackItem.type === FeedbackType.HumanReviewerFeedback;
  const isReviewer = reviewLevel !== ReviewLevel.Attempt;
  const isFeedbackItemOwner = feedbackItem.author === taskContext.workerId;

  // We don't want to display the action buttons ("Fix" and "Disagree") for blocking automated feedback
  // since they are blocking feedback items, and CBs don't have the choice to disagree with them.
  const isBlockingAutomatedFeedback = !isWorkerComment && feedbackItem.isBlocking;

  /**
   * Either address feedback actions are enabled or edit feedback actions are enabled, but not both.
   */
  const isAddressWorkerFeedbackActionsEnabled =
    isWorkerComment && feedbackItem.author !== taskContext.workerId;

  const isAddressAutomatedFeedbackActionsEnabled = !isBlockingAutomatedFeedback && !isWorkerComment;
  const isEditFeedbackActionsEnabled =
    isWorkerComment && isReviewer && isFeedbackItemOwner && feedbackItem.childId;

  const isJustificationRequired = feedbackItem.isJustificationRequired;
  const isOverallFeedback = !feedbackItem.childId;

  return (
    <div className={className}>
      {!isWorkerComment && (
        <BinaryFeedbackGroup
          helpfulness={feedbackItem.helpfulness}
          handleSubmitFeedback={_.throttle(helpfulness => {
            if (helpfulness === feedbackItem.helpfulness) {
              return;
            }

            setFeedback(
              produce(feedback, draft => {
                const targetFeedbackItem = draft.find(item => item.id === feedbackItem.id)!;
                targetFeedbackItem.helpfulness = helpfulness;
              }),
            );

            onFeedbackRating && onFeedbackRating(helpfulness);
          }, 1000)}
        />
      )}
      {isJustificationRequired && (
        <div className="pt-2">
          <TextArea
            helperText={
              justification.length < JUSTIFICATION_MIN_CHARACTERS
                ? `Justification must be at least ${JUSTIFICATION_MIN_CHARACTERS} characters long`
                : ''
            }
            onChange={e => setJustification(e.target.value)}
            value={justification}
            error={justification.length < JUSTIFICATION_MIN_CHARACTERS}
            placeholder="Enter a justification for dismissing this comment"
            rows={3}
          />
        </div>
      )}
      {isAddressWorkerFeedbackActionsEnabled && (
        <>
          <div className={twMerge('flex justify-start mt-3', isOverallFeedback && 'justify-end')}>
            <BinaryFeedbackGroup
              message="Was this feedback helpful?"
              helpfulness={
                feedbackItem.action?.actionTaken === ActionTakenEnum.Fixed
                  ? 'helpful'
                  : feedbackItem.action?.actionTaken === ActionTakenEnum.Disagreed
                  ? 'not_helpful'
                  : undefined
              }
              handleSubmitFeedback={_.throttle(helpfulness => {
                if (helpfulness === feedbackItem.helpfulness) {
                  return;
                }
                const feedbackResponseType =
                  helpfulness === 'helpful'
                    ? FeedbackResponseType.Accept
                    : FeedbackResponseType.Dismiss;

                if (feedbackItem.sourceFeedbackId) {
                  addFeedbackResponseToWorkerComment(
                    feedbackItem.sourceFeedbackId,
                    feedbackResponseType,
                  );
                }

                const feedbackAction =
                  helpfulness === 'helpful' ? ActionTakenEnum.Fixed : ActionTakenEnum.Disagreed;

                setFeedback(
                  produce(feedback, draft => {
                    const targetFeedbackItem = draft.find(item => item.id === feedbackItem.id)!;
                    targetFeedbackItem.action = {
                      ...targetFeedbackItem.action,
                      actionTaken: feedbackAction,
                    };
                  }),
                );

                onFeedbackAction && onFeedbackAction(feedbackAction);
              }, 1000)}
            />
          </div>
        </>
      )}

      {isAddressAutomatedFeedbackActionsEnabled && (
        <>
          <div className="flex flex-row justify-start mt-3">
            <Button
              className="mr-1 px-2 h-6 text-xs"
              variant={
                feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed ? 'neutral' : 'white'
              }
              onClick={_.throttle(() => {
                if (feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed) {
                  return;
                }

                setFeedback(
                  produce(feedback, draft => {
                    const targetFeedbackItem = draft.find(item => item.id === feedbackItem.id)!;
                    targetFeedbackItem.action = {
                      ...targetFeedbackItem.action,
                      actionTaken: ActionTakenEnum.Dismissed,
                    };
                  }),
                );

                onFeedbackAction && onFeedbackAction(ActionTakenEnum.Dismissed);
              }, 1000)}
              disabled={
                feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed ||
                (isJustificationRequired && justification.length < JUSTIFICATION_MIN_CHARACTERS)
              }
              icon={
                feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed
                  ? {
                      type: ['far', 'check'],
                    }
                  : undefined
              }
            >
              {feedbackItem.action?.actionTaken === ActionTakenEnum.Dismissed
                ? 'Dismissed'
                : 'Dismiss'}
            </Button>
          </div>
        </>
      )}

      {isEditFeedbackActionsEnabled && (
        <div className="flex flex-row justify-end mt-3">
          <Button
            className="mr-1 px-2 h-6 text-xs"
            variant="white"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setIsEditing && setIsEditing(true);
              if (setIsEditing && feedbackItem.sourceFeedbackId) {
                selectWorkerComment(feedbackItem.sourceFeedbackId);
              }
            }}
            icon={{
              type: ['far', 'edit'],
            }}
          >
            Edit
          </Button>
          <Button
            className="mr-1 px-2 h-6 text-xs"
            variant="white"
            icon={{
              type: ['far', 'trash-alt'],
            }}
            onClick={e => {
              if (feedbackItem.sourceFeedbackId) {
                e.stopPropagation();
                e.preventDefault();
                if (!window.confirm('Are you sure you want to delete this comment?')) {
                  return;
                }
                deleteWorkerComment(feedbackItem.sourceFeedbackId);
              }
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

interface FeedbackBodyProps {
  className?: string;
  content: string;
  shouldUseMarkdownPreview?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  onFeedbackAction?: (action: ActionTakenEnum) => void;
  onFeedbackRating?: (rating: FeedbackHelpfulness) => void;
}

const FeedbackBody: React.FC<FeedbackBodyProps> = ({
  className,
  content,
  shouldUseMarkdownPreview,
  setIsEditing,
  onFeedbackAction,
  onFeedbackRating,
}) => {
  return (
    <>
      {shouldUseMarkdownPreview ? (
        <ChatMarkdown className={twMerge('text-xs', className)} markdown={content} />
      ) : (
        <Typography.Paragraph3
          className={twMerge('text-neutral-800 break-words whitespace-pre-wrap', className)}
        >
          {content}
        </Typography.Paragraph3>
      )}
      <FeedbackActions
        setIsEditing={setIsEditing}
        onFeedbackAction={onFeedbackAction}
        onFeedbackRating={onFeedbackRating}
      />
    </>
  );
};

interface FeedbackItemHeaderProps {
  className?: string;
  titleClassName?: string;
  title: string;
  isFeedbackAddressed?: boolean;
  authorLabel?: string;
  isWorkerComment: boolean;
  isSeniorReview?: boolean;
  isAccordionExpanded?: boolean;
  isFeedbackItemOwner?: boolean;
  isOverallFeedback?: boolean;
}

const FeedbackItemHeader: React.FC<FeedbackItemHeaderProps> = ({
  className,
  titleClassName,
  title,
  isFeedbackAddressed,
  authorLabel,
  isWorkerComment,
  isSeniorReview,
  isAccordionExpanded,
  isFeedbackItemOwner,
  isOverallFeedback,
}) => {
  const canShowReviewerIdAndBadge =
    !isFeedbackItemOwner && !isOverallFeedback && isWorkerComment && isAccordionExpanded;
  const [headerContainerRef, { width: headerContainerWidthPx }] = useMeasure();

  const headerTitle = useMemo(() => {
    // When the feedback item accordion is expanded, we show the author as the header title instead of the `title`
    if (canShowReviewerIdAndBadge && authorLabel) {
      if (isOverallFeedback) {
        return `${authorLabel} - ${title}`;
      }
      return authorLabel;
    }

    return title;
  }, [authorLabel, canShowReviewerIdAndBadge, isOverallFeedback, title]);

  return (
    <div
      ref={headerContainerRef}
      className={twMerge('flex flex-row justify-between items-center flex-grow', className)}
    >
      <div className="flex flex-row justify-start items-center">
        <div
          style={{
            maxWidth: headerContainerWidthPx - 20,
          }}
        >
          <Typography.Subtitle4 className={twMerge('text-left', titleClassName)}>
            {headerTitle}
          </Typography.Subtitle4>
        </div>
        {canShowReviewerIdAndBadge && isSeniorReview && (
          <Icon
            icon={['fas', 'badge-check']}
            variant={isFeedbackAddressed ? 'secondary' : 'primary'}
            className="text-neutral-500"
            size="sm"
            tooltip="This feedback was left by a senior reviewer"
            tooltipProps={{ side: 'top' }}
          />
        )}
      </div>
      {!isFeedbackItemOwner && !isFeedbackAddressed && !isAccordionExpanded && (
        <FeedbackStatusDot />
      )}
    </div>
  );
};

interface FeedbackEditViewProps {
  className?: string;
  initialContent: string;
  onSave: (commentText: string) => void;
  onCancel: () => void;
}

const FeedbackEditView: React.FC<FeedbackEditViewProps> = ({
  className,
  initialContent,
  onSave,
  onCancel,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [editedCommentText, setEditedCommentText] = useState<string>(initialContent);

  const handleTextAreaInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedCommentText(e.target.value);
  };
  return (
    <div className={twMerge('flex flex-col', className)}>
      <TextArea
        className="pb-2"
        ref={textareaRef}
        id="dynamic-textarea"
        rows={1}
        onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
          const textArea = e.target;
          textArea.style.height = 'auto';
          textArea.style.height = textArea.scrollHeight + 'px';
          textArea.style.maxHeight = '16rem';
        }}
        autoFocus
        dynamic
        resizable={false}
        onChange={handleTextAreaInputChange}
        value={editedCommentText}
        placeholder="Enter your comment"
      />
      <div className="flex flex-row justify-between pb-2">
        <Button
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onCancel();
          }}
          size="sm"
          variant="neutral"
        >
          Cancel
        </Button>
        <Button
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onSave(editedCommentText);
          }}
          size="sm"
          variant="primary"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

interface FeedbackAccordionProps {
  feedbackItem: ContributorFeedbackItem;
}

export const FeedbackAccordionContext = createContext<FeedbackAccordionProps | null>(null);

// eslint-disable-next-line complexity
const FeedbackAccordion: React.FC<FeedbackAccordionProps> = ({ feedbackItem }) => {
  const { title, content, type, childId } = feedbackItem;
  const taskContext = useContext(ChatTaskContext);
  const theme = useChatThemeStore(state => state.theme);

  // Default expand feedback for automated feedback items and feedback from other reviewers
  const [expanded, setExpanded] = useState<boolean>(
    type !== FeedbackType.HumanReviewerFeedback || feedbackItem.author !== taskContext.workerId,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const updateWorkerComment = useWorkerCommentsStore(state => state.updateWorkerComment);
  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const isWorkerComment = feedbackItem.type === FeedbackType.HumanReviewerFeedback;
  const isReviewer = reviewLevel !== ReviewLevel.Attempt;
  const isFeedbackItemOwner = feedbackItem.author === taskContext.workerId;
  const isFeedbackAddressed = !!feedbackItem.action?.actionTaken;
  const isOverallFeedback = !childId;
  const shouldFadeText = isFeedbackAddressed || isFeedbackItemOwner;

  const projectId = taskContext.projectId;

  const shouldUseMarkdownLinters = useUnleashFeatureFlag(
    'chat-enable-markdown-linters',
    { projectId },
    false,
  );

  // We use the last 6 chars to identify the author
  const authorTag = feedbackItem.author?.slice(-6);
  const authorLabel = authorTag && `Reviewer (${authorTag})`;

  let isSeniorReview;
  if (reviewLevel != null) {
    isSeniorReview = SENIOR_LABEL_REVIEW_LEVELS.includes(reviewLevel);
  }

  useEffect(() => {
    if (feedbackItem.type !== FeedbackType.HumanReviewerFeedback) {
      return;
    }

    if (selectedWorkerComment === feedbackItem.sourceFeedbackId) {
      if (!expanded) {
        setExpanded(true);
      }

      return;
    }

    // Necessary because eslint expects `expanded` as a dependency when it shouldn't, as
    // including it would result in an infinite useEffect

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkerComment]);

  const onFeedbackAction = useCallback(
    async (action: ActionTakenEnum) => {
      setExpanded(false);

      // Normally, Snowflake events for feedback actions are logged on step submission (i.e `usePerStepContributorFeedback`).
      // However, for ChatBulkAudit, we need to log the events immediately when the feedback action is taken because steps
      // aren't submitted in CBA if it's in read-only mode. Will need to follow-up here to figure out a better way of handling this.
      if (taskContext.contextSource === 'ChatBulkAudit') {
        await logEvent(Event.UnifiedFeedbackContributorActionEvent, {
          projectId: taskContext.projectId,
          workerId: taskContext.workerId,
          assignmentId: taskContext.assignmentId,
          action,
          feedbackType: feedbackItem.type,
          feedbackItem,
        } as UnifiedFeedbackContributorActionEvent);
      }
    },
    [
      feedbackItem,
      taskContext.assignmentId,
      taskContext.contextSource,
      taskContext.projectId,
      taskContext.workerId,
    ],
  );

  const onFeedbackRating = useCallback(
    async (rating: FeedbackHelpfulness) => {
      if (taskContext.contextSource === 'ChatBulkAudit') {
        await logEvent(Event.UnifiedFeedbackContributorRatingEvent, {
          projectId: taskContext.projectId,
          workerId: taskContext.workerId,
          assignmentId: taskContext.assignmentId,
          rating,
          feedbackType: feedbackItem.type,
          feedbackItem,
        } as UnifiedFeedbackContributorRatingEvent);
      }
    },
    [
      feedbackItem,
      taskContext.assignmentId,
      taskContext.contextSource,
      taskContext.projectId,
      taskContext.workerId,
    ],
  );

  // Darkmode support is built-in for standard `bg-` tailwind classes (i.e bg-neutral-200).
  // Since we're using custom colors, we need to handle darkmode manually.
  const bgColor = theme === ChatTheme.Light ? 'bg-[#0011FF0F]' : 'bg-[#6A53FF4D] ';

  const shouldUseMarkdownPreview =
    taskContext.contextSource === 'ChatBulkAudit' || shouldUseMarkdownLinters;

  return (
    <FeedbackAccordionContext.Provider value={{ feedbackItem }}>
      <Accordion
        className={twMerge(
          'w-full bg-transparent shadow-none border-none rounded-none',
          /**
           * 'bgColor' is used to highlight feedback items from other reviewers that have not been addressed.
           * When the feedback item is collapsed, we get rid of the background color and we use a status dot instead
           * to indicate that the feedback item has not been addressed.
           */
          !isFeedbackAddressed && !isFeedbackItemOwner && expanded && bgColor,
        )}
        iconClassName="invisible"
        buttonClassName="py-2"
        header={
          <div className="flex flex-row flex-1">
            <FeedbackItemHeader
              className={twMerge('flex-1 w-full')}
              titleClassName={twMerge(shouldFadeText && 'text-neutral-500')}
              title={title}
              isFeedbackAddressed={!!isFeedbackAddressed}
              authorLabel={authorLabel}
              isWorkerComment={isWorkerComment}
              isSeniorReview={isSeniorReview}
              isAccordionExpanded={expanded}
              isFeedbackItemOwner={isFeedbackItemOwner}
              isOverallFeedback={isOverallFeedback}
            />
          </div>
        }
        target={
          <div>
            {authorLabel && isOverallFeedback && !isFeedbackItemOwner && (
              <div className="flex flex-row justify-start items-center">
                <Typography.Subtitle4
                  className={twMerge(
                    'text-neutral-700 text-[10px]',
                    shouldFadeText && 'text-neutral-500',
                  )}
                >
                  {authorLabel}
                </Typography.Subtitle4>
                {isSeniorReview && (
                  <Icon
                    icon={['fas', 'badge-check']}
                    variant={isFeedbackAddressed ? 'secondary' : 'primary'}
                    className="text-neutral-500"
                    size="sm"
                    tooltip="This feedback was left by a senior reviewer"
                    tooltipProps={{ side: 'top' }}
                  />
                )}
              </div>
            )}
            {feedbackItem.inlinePosition?.quotedText && (
              <div className="my-2 border-l-2 border-l-neutral-300">
                <Typography.Subtitle4 className="pl-2 text-neutral-500 whitespace-pre-wrap">
                  {truncateText(feedbackItem.inlinePosition.quotedText, 150)}
                </Typography.Subtitle4>
              </div>
            )}

            {isWorkerComment && isEditing && isReviewer && isFeedbackItemOwner ? (
              <FeedbackEditView
                initialContent={content}
                className="my-2"
                onCancel={() => setIsEditing(false)}
                onSave={editedComment => {
                  if (feedbackItem.sourceFeedbackId) {
                    setIsEditing(false);
                    updateWorkerComment(
                      feedbackItem.sourceFeedbackId,
                      editedComment,
                      truncateText(editedComment, 30),
                    );
                  }
                }}
              />
            ) : (
              <FeedbackBody
                className={shouldFadeText ? 'text-neutral-500' : ''}
                content={content}
                // Only Chat Bulk Audit really makes use of the markdown preview
                shouldUseMarkdownPreview={shouldUseMarkdownPreview}
                setIsEditing={setIsEditing}
                onFeedbackAction={onFeedbackAction}
                onFeedbackRating={onFeedbackRating}
              />
            )}
          </div>
        }
        onChange={(_e, isExpanded) => {
          if (isWorkerComment) {
            if (feedbackItem.sourceFeedbackId && isExpanded) {
              // Let the useEffect that depends on selected WC above handle `expanded` state
              selectWorkerComment(feedbackItem.sourceFeedbackId);
              return;
            }
            selectWorkerComment(null);
          }
          setExpanded(prev => !prev);
        }}
        expanded={expanded}
      />
    </FeedbackAccordionContext.Provider>
  );
};

export default memo(FeedbackAccordion);
